// A/B tests (spec/dashboard.md section 8.4): cards over SmartObjects.Analytics.ABTest with the status chip, the audience,
// the variants table with the users and revenue of the selected environment, the lifecycle as staged status changes,
// the variant overrides (one value input per variant) and the winner applied as one commit through `:apply`. M7 slice 7: the
// results read with ?branch= (the control first, the SRM check) fills sessions, playtime, conversion and ARPU, the exposed
// figure, the Users-per-day chart, the significance line per variant and the SRM chip, each with its definition. The UX
// review: the lifecycle buttons carry the note "Staged: live after Save All and a release is pinned on the environment."
// ("Save first" leads while the test has unsaved drafts), Stop test opens one "Stop test" dialog (the variant as a choice,
// then Finish without change or Apply <variant>), and the chips' tooltips are the ABTest help of content/help.ts. The A/B
// wizard (8.4; AbTestWizard): "New A/B test…" leads the Create menu ("Blank A/B test" keeps the bare path), and the results
// lead with the test's primary metric (its `metric`; conversion when empty) with its lift, interval and p, the other figures
// as secondary rows, and the progress line against the required sample (spec/liveops-engine.md 11.2).
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import type { Document, Json } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { baseOf } from '../../content/actions';
import { CardList, type CardContext } from '../../content/cards/CardList';
import { ConditionSummary } from '../../content/conditions/ConditionBuilder';
import { collectTree } from '../../content/conditions/ops';
import { useContent } from '../../content/ContentContext';
import { formatValue } from '../../content/format';
import { EnvSelector, useLiveOpsEnv } from '../../content/liveops/env';
import { n, useComputedText, useDefinitionText } from '../../content/liveops/analytics';
import { applyAbTest, countText, headlineOf, metricOf, moneyText, plannerOf, progressOf, useAbTestResults, type AbTestResults } from '../../content/liveops/stats';
import { addVariant, createAbTest, createVariantOverride } from '../../content/overrides/create';
import { AB_TEST, OVERRIDE_INFO, groupsOf, namesTest, paramAt, patchOf, resolveDocument, toGroupJson } from '../../content/overrides/model';
import { ParameterPickDialog } from '../../content/overrides/ParameterPickDialog';
import { useDocumentList } from '../../content/useDocumentList';
import { FieldWidget } from '../../content/widgets/FieldWidget';
import { ReferencePicker } from '../../content/widgets/ReferenceField';
import { useEditorHost, useWidgetEnv } from '../../content/widgets/context';
import { useGame } from '../../shell/GameContext';
import { Button, IconButton } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { useConfirm } from '../../ui/ConfirmDialog';
import { AlertTriangle, CheckCircle2, Filter, Hourglass, Layers, Play, Plus, Square, X } from '../../ui/icons';
import { CommitTextInput, NumberInput, Switch } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { LineChart } from '../../ui/Sparkline';
import { formatDuration } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { useAnalyticsRange } from '../../workspace/analyticsRange';
import { useWorkspaceVersion } from '../../workspace/react';
import { AbTestWizard } from './AbTestWizard';
import { BodyField, Chips, CommonTail, NameText, paramsOf, UsersBadge } from './cards';
import './calendar.css';
import './liveops.css';

export const AB_STATUS: Record<number, { label: string; text: string; tone: 'neutral' | 'success' | 'info' | 'accent' }> = {
  0: { label: 'Inactive', text: 'not running', tone: 'neutral' },
  1: { label: 'Active', text: 'collecting users & data', tone: 'success' },
  4: { label: 'Collect', text: 'collecting data', tone: 'info' },
  2: { label: 'Finished', text: 'finished', tone: 'neutral' },
  3: { label: 'Finished without change', text: 'finished, no change', tone: 'neutral' },
};

const AB_RULE = [{ field: 'data.flag', op: 'eq' as const, value: '1' }];

export function AbTestsPage() {
  const { env } = useLiveOpsEnv();
  const wenv = useWidgetEnv();
  const overrides = useDocumentList({ template: OVERRIDE_INFO, includeHeirs: false, rules: AB_RULE, pageSize: 200 });
  const [wizard, setWizard] = useState(false);
  // the card list's editor host is inside CardList: a child opens the staged test there
  const [opening, setOpening] = useState<string | null>(null);
  return (
    <CardList
      id="ab-tests"
      title="A/B tests"
      template={AB_TEST}
      includeHeirs={false}
      toolbar={
        <>
          <EnvSelector />
          <TestersSwitch />
        </>
      }
      header={(ctx) => <TestHeader ctx={ctx} overrides={overrides.rows} />}
      body={(ctx) => <TestBody ctx={ctx} overrides={overrides.rows} envSlug={env?.slug ?? null} />}
      createExtras={[
        { key: 'wizard', label: 'New A/B test…', run: () => setWizard(true) },
        {
          key: 'new-test',
          label: 'Blank A/B test',
          run: () => {
            const { testId } = createAbTest(wenv, 'New test');
            setOpening(testId);
          },
        },
      ]}
      filterFields={[{ key: 'data.status', label: 'Status', type: 'enum', group: 'Test', options: Object.entries(AB_STATUS).map(([v, s]) => ({ value: v, label: s.label })) }]}
      emptyText="No A/B tests yet. A test splits the players who pass its condition into variants by weight; each variant can override values — Control keeps them. A new test is Inactive until Run test and a Save All."
    >
      {wizard && <AbTestWizard onClose={() => setWizard(false)} onCreated={setOpening} />}
      {opening && <OpenCard id={opening} onDone={() => setOpening(null)} />}
    </CardList>
  );
}

/** Opens a document in the card list's editor host (rendered inside CardList, where the host is). */
function OpenCard({ id, onDone }: { id: string; onDone: () => void }) {
  const host = useEditorHost();
  useEffect(() => {
    void host?.open(id);
    onDone();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function statusOf(doc: Document): number {
  return Number((doc.data as Record<string, Json>).status ?? 0);
}

/** A titled group of the expanded body; `single` hides the lone field's label visually (the title says it). */
function Group({ title, single, children }: { title: string; single?: boolean; children: ReactNode }) {
  return (
    <section className={`lo-group ${single ? 'single' : ''}`} aria-label={title}>
      <h4 className="lo-group-title">{title}</h4>
      {children}
    </section>
  );
}

/** The override documents of a test (OverrideInfo with flag 1 whose groups name the test). */
function useTestOverrides(overrides: Document[], testId: string): Document[] {
  const { catalog } = useWidgetEnv();
  return useMemo(() => overrides.filter((o) => groupsOf(o).some((g) => namesTest(catalog, g.condition, testId) !== null)), [overrides, catalog, testId]);
}

function variantIdsOf(doc: Document): string[] {
  const d = (doc.data ?? {}) as Record<string, Json>;
  return Array.isArray(d.variants) ? d.variants.filter((v): v is string => typeof v === 'string') : [];
}

/** The card's head: the name, the state as a word, the users badge, the audience; the lifecycle actions on the right. */
function TestHeader({ ctx, overrides }: { ctx: CardContext; overrides: Document[] }) {
  const catalog = useContent();
  const params = paramsOf(AB_TEST);
  const d = (ctx.doc.data ?? {}) as Record<string, Json>;
  const status = AB_STATUS[statusOf(ctx.doc)] ?? AB_STATUS[0]!;
  const best = typeof d.bestVariant === 'string' ? catalog.resolve(d.bestVariant) : undefined;
  const mine = useTestOverrides(overrides, ctx.doc.id);
  return (
    <div className="card-head-lines">
      <div className="lo-head-line lo-ab-head">
        <NameText doc={ctx.doc} />
        <Badge tone={status.tone} title={status.text}>
          {status.label}
          {statusOf(ctx.doc) === 2 && best ? ` · best: ${catalog.label(best)}` : ''}
        </Badge>
        <UsersBadge kind="ab_test" id={ctx.doc.id} />
        <span className="lo-cond">
          <Filter aria-hidden="true" />
          <ConditionSummary rootId={typeof d.condition === 'string' ? d.condition : null} />
        </span>
        <span className="spacer" />
        <Lifecycle ctx={ctx} variantIds={variantIdsOf(ctx.doc)} overrides={mine} />
      </div>
      <Chips ctx={ctx} params={params} names={['targetUsers', 'usersType', 'concurrentTest', 'preInitLaunch', 'finishTime']} zero={{ finishTime: 'no end' }} />
    </div>
  );
}

function TestBody({ ctx, overrides, envSlug }: { ctx: CardContext; overrides: Document[]; envSlug: string | null }) {
  const env = useWidgetEnv();
  const { workspace } = env;
  useWorkspaceVersion(workspace);
  const { environments } = useGame();
  const { branch = '' } = useParams();
  const environment = environments.find((e) => e.slug === envSlug) ?? null;
  const { range } = useAnalyticsRange(environment);
  const results = useAbTestResults(environment, ctx.doc.id, { branch, includeTesters: range.includeTesters });
  const params = paramsOf(AB_TEST);
  const variantIds = variantIdsOf(ctx.doc);
  const mine = useTestOverrides(overrides, ctx.doc.id);
  const meta = (ctx.doc.meta ?? {}) as Record<string, unknown>;
  const comment = typeof meta.comment === 'string' ? meta.comment : '';
  return (
    <div className="card-fields lo-body solo lo-ab-body">
      <div className="card-fields-main">
        <Group title="Variants">
          <VariantsTable ctx={ctx} variantIds={variantIds} results={results.data} />
        </Group>
        {results.data && (
          <Group title="Results">
            <TestStatistics test={ctx.doc} variantIds={variantIds} results={results.data} />
          </Group>
        )}
        <Group title="Audience" single>
          <BodyField ctx={ctx} params={params} name="condition" />
        </Group>
        <Group title="Override references">
          <OverrideReferences ctx={ctx} variantIds={variantIds} overrides={mine} />
        </Group>
        <Group title="Advanced">
          <div className="form-grid">
            <BodyField ctx={ctx} params={params} name="salt" help="Advanced: a new salt re-buckets every player not yet assigned" />
            <Field label="Comments">
              <CommitTextInput value={comment} multiline placeholder="Add a comment…" disabled={!ctx.canWrite} aria-label="Comments" onCommit={(v) => ctx.edit('meta', v.trim() ? { ...meta, comment: v } : Object.fromEntries(Object.entries(meta).filter(([k]) => k !== 'comment')))} />
            </Field>
          </div>
        </Group>
        <Group title="Notes">
          <CommonTail ctx={ctx} />
        </Group>
      </div>
    </div>
  );
}

/** Run test → Finish collecting users → Stop test → Select a variant → Apply results (the command) or Finish without change. */
function Lifecycle({ ctx, variantIds, overrides }: { ctx: CardContext; variantIds: string[]; overrides: Document[] }) {
  const { catalog, workspace } = useWidgetEnv();
  useWorkspaceVersion(workspace);
  const confirm = useConfirm();
  const toasts = useToasts();
  const { client } = useAuth();
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  const [stopping, setStopping] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const status = statusOf(ctx.doc);
  const now = () => Math.floor(Date.now() / 1000);
  const run = () => ctx.edit('data.status', 1);
  const collect = async () => {
    if (await confirm({ title: 'Stop adding users?', body: 'Stops adding new players to the test; keeps collecting data for the players already in. The change is staged; Save All commits it.', confirmLabel: 'Stop adding users' })) ctx.edit('data.status', 4);
  };
  const unsaved = () => {
    const ids = new Set<string>([ctx.doc.id, ...variantIds]);
    for (const o of overrides) {
      ids.add(o.id);
      for (const g of groupsOf(o)) {
        for (const r of g.overrides) ids.add(r);
        if (g.condition) for (const id of collectTree(catalog, g.condition).ids) ids.add(id);
      }
    }
    return workspace.all.some((dr) => dr.kind === 'document' && ids.has(dr.id));
  };
  const finishWithoutChange = (variant: string) => {
    ctx.edit('data.status', 3);
    ctx.edit('data.bestVariant', variant);
    ctx.edit('data.finishTime', now());
    close();
  };
  const openStop = () => {
    setChosen(variantIds[0] ?? null);
    setStopping(true);
  };
  const close = () => {
    setChosen(null);
    setStopping(false);
  };
  const nameOf = (v: string) => {
    const doc = catalog.resolve(v);
    const name = (doc?.data as Record<string, Json> | undefined)?.name;
    return typeof name === 'string' && name ? name : doc ? catalog.label(doc) : v.slice(0, 8);
  };
  const apply = async (variant: string) => {
    if (unsaved()) {
      toasts.warning('Save first: the test, its variants or its overrides have unsaved changes; the apply commits what the server has.');
      return;
    }
    setBusy(true);
    try {
      const r = await applyAbTest(client, org, game.slug, branch, ctx.doc.id, variant);
      toasts.success(`Applied ${r.applied.length} value${r.applied.length === 1 ? '' : 's'} in commit ${r.commit.slice(0, 7)}${r.skipped.length ? `; ${r.skipped.length} skipped` : ''}. Revert it from the history page.`);
      close();
      ctx.reload?.();
    } catch (e) {
      toasts.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  if (!ctx.canWrite) return null;
  const staged = status === 0 || status === 1 || status === 4;
  const dirty = staged && unsaved();
  return (
    <div className="lifecycle lo-ab-lifecycle" data-testid="ab-lifecycle">
      {status === 0 && (
        <Button size="s" onClick={run} icon={<Play aria-hidden="true" />} title="Stages Active: once saved and released, the players who pass the condition are bucketed into the variants">
          Run test
        </Button>
      )}
      {status === 1 && (
        <Button size="s" onClick={() => void collect()} icon={<Hourglass aria-hidden="true" />} title="Stages Collect: no new players join; the players already in stay">
          Finish collecting users
        </Button>
      )}
      {(status === 1 || status === 4) && (
        <Button size="s" onClick={openStop} icon={<Square aria-hidden="true" />} title="Choose the best variant, then finish without change (staged) or apply it (one commit)">
          Stop test
        </Button>
      )}
      {staged && (
        <p className="lo-ab-staged" data-testid="ab-staged-note">
          {dirty && <strong className="lo-ab-save-first">Save first: this test has unsaved changes. </strong>}
          Staged: live after Save All and a release is pinned on the environment.
        </p>
      )}
      {stopping && (
        <Modal
          title="Stop test"
          open
          onClose={close}
          size="s"
          footer={
            <>
              <Button onClick={close}>Cancel</Button>
              <span className="spacer" />
              <Button onClick={() => chosen && finishWithoutChange(chosen)} disabled={!chosen}>
                Finish without change
              </Button>
              <Button variant="primary" busy={busy} disabled={!chosen} onClick={() => chosen && void apply(chosen)}>
                {chosen ? `Apply ${nameOf(chosen)}` : 'Apply'}
              </Button>
            </>
          }
        >
          <fieldset className="lo-ab-choose" aria-label="Best variant">
            <legend>The best variant</legend>
            {variantIds.map((v) => (
              <label key={v} className="lo-ab-choice">
                <input type="radio" name={`best-${ctx.doc.id}`} value={v} checked={chosen === v} onChange={() => setChosen(v)} />
                {nameOf(v)}
              </label>
            ))}
          </fieldset>
          <p>
            <strong>Finish without change</strong> stages the finish (status, best variant, finish time) and leaves the documents as they are; it goes live after Save All and a release pin.
          </p>
          <p>
            <strong>Apply</strong> writes the variant's override values into the base documents, finishes the test and un-exports its override groups — one commit the history page can revert. It needs the test saved first.
          </p>
        </Modal>
      )}
    </div>
  );
}

/** The shared "Include testers" choice (the analytics range's), on the A/B toolbar: testers are excluded by default (Q-J-11). */
function TestersSwitch() {
  const { env } = useLiveOpsEnv();
  const { range, setIncludeTesters } = useAnalyticsRange(env);
  return <Switch label="Include testers" checked={range.includeTesters} onChange={setIncludeTesters} />;
}

type VariantResult = AbTestResults['variants'][number];

/** `+3.1` / `−0.4` / `0.0` (a real minus sign). */
export function signed(x: number, digits = 1): string {
  const text = Math.abs(x).toFixed(digits);
  if (Number(text) === 0) return (0).toFixed(digits);
  return `${x > 0 ? '+' : '−'}${text}`;
}

/** `0.08`, `0.004`, `< 0.001`, `n/a` (the p-value stat). */
function pValue(p: number | string | null | undefined): string {
  if (p === null || p === undefined) return 'n/a';
  const v = Number(p);
  if (v < 0.001) return '< 0.001';
  return v < 0.01 ? v.toFixed(3) : v.toFixed(2);
}

/** `p = 0.08`, `p = 0.004`, `p < 0.001`. */
export function pText(p: number | string | null | undefined): string {
  if (p === null || p === undefined) return 'p n/a';
  const v = Number(p);
  if (v < 0.001) return 'p < 0.001';
  return `p = ${v < 0.01 ? v.toFixed(3) : v.toFixed(2)}`;
}

function conversionText(r: VariantResult): string {
  if (r.conversion === null || r.conversion === undefined) return `— (${n(r.converted)}/${n(r.users)})`;
  return `${(Number(r.conversion) * 100).toFixed(1)} % (${n(r.converted)}/${n(r.users)})`;
}

function VariantsTable({ ctx, variantIds, results }: { ctx: CardContext; variantIds: string[]; results: AbTestResults | null }) {
  const env = useWidgetEnv();
  const { catalog, workspace } = env;
  const define = useDefinitionText();
  const computed = useComputedText();
  const defs = results?.definitions ?? null;
  const at = results?.computedAt ?? null;
  const total = variantIds.reduce((s, id) => s + Number(((catalog.resolve(id)?.data ?? {}) as Record<string, Json>).weight ?? 0), 0);
  const editVariant = (id: string, field: string, value: Json) => {
    const node = catalog.resolve(id) as Document | undefined;
    workspace.edit('document', id, `data.${field}`, value, { base: node ? baseOf(node) : undefined, label: node ? catalog.label(node) : 'variant', template: node?.template });
  };
  const removeVariant = (id: string) => {
    const node = catalog.resolve(id) as Document | undefined;
    workspace.remove('document', id, node ? baseOf(node) : undefined, { label: node ? catalog.label(node) : 'variant', template: node?.template });
    ctx.edit('data.variants', variantIds.filter((v) => v !== id));
  };
  const pending = results ? '—' : '…';
  return (
    <div className="variants variants-m7 lo-ab-table" role="table" aria-label="Variants" data-testid="ab-variants">
      <div className="variant-row variant-head" role="row">
        <span role="columnheader">Variant</span>
        <span role="columnheader">Weight</span>
        <span role="columnheader">Share</span>
        <span role="columnheader" title={define(defs, ['users', 'exposed'], at)}>
          Users
        </span>
        <span role="columnheader" title={define(defs, ['revenue'], at, ['Claimed, non-test orders of the assigned players since their assignment, per currency'])}>
          Revenue
        </span>
        <span role="columnheader" title={define(defs, ['sessions'], at)}>
          Sessions
        </span>
        <span role="columnheader" title={define(defs, ['sessions'], at)}>
          Playtime
        </span>
        <span role="columnheader" title={define(defs, ['converted'], at)}>
          Conversion
        </span>
        <span role="columnheader" title={define(defs, ['arpu'], at)}>
          ARPU
        </span>
        <span role="columnheader" />
      </div>
      {variantIds.length === 0 && (
        <div className="lo-ab-empty-row" role="row">
          <span role="cell">No variants: a test needs a control and at least one other variant.</span>
        </div>
      )}
      {variantIds.map((id) => {
        const node = catalog.resolve(id) as Document | undefined;
        const vd = (node?.data ?? {}) as Record<string, Json>;
        const weight = Number(vd.weight ?? 0);
        const r = results?.variants.find((v) => v.variantId === id);
        return (
          <div key={id} className="variant-row" role="row" data-testid={`variant-${id}`}>
            <span role="cell">
              <CommitTextInput size="s" value={typeof vd.name === 'string' ? vd.name : ''} disabled={!ctx.canWrite} aria-label={`Variant ${id.slice(0, 8)} name`} onCommit={(v) => editVariant(id, 'name', v)} />
            </span>
            <span role="cell">
              <NumberInput size="s" kind="int" value={weight} disabled={!ctx.canWrite} aria-label={`Variant ${id.slice(0, 8)} weight`} onCommit={(v) => editVariant(id, 'weight', Number(v ?? 0))} />
            </span>
            <span role="cell" className="lo-num-cell">{total > 0 ? `${((weight / total) * 100).toFixed(0)}%` : '—'}</span>
            <span role="cell" className="lo-num-cell" data-testid={`variant-users-${id}`}>
              {r ? `${n(r.users)}${n(r.finished) ? ` (${n(r.finished)} finished)` : ''} · exposed ${n(r.exposed)}` : results ? '0' : '…'}
            </span>
            <span role="cell" className="lo-num-cell">{r ? (r.revenue.length ? r.revenue.map((m) => moneyText(Number(m.amount), m.currency)).join(', ') : '—') : pending}</span>
            <span role="cell" className="lo-num-cell" data-testid={`variant-sessions-${id}`}>
              {r ? String(n(r.sessions)) : pending}
            </span>
            <span role="cell" className="lo-num-cell" data-testid={`variant-playtime-${id}`}>
              {r ? (n(r.playtimeSeconds) ? formatDuration(n(r.playtimeSeconds)) : '0') : pending}
            </span>
            <span role="cell" className="lo-num-cell" data-testid={`variant-conversion-${id}`}>
              {r ? conversionText(r) : pending}
            </span>
            <span role="cell" className="lo-num-cell" data-testid={`variant-arpu-${id}`} title={r && r.arppu?.length ? `ARPPU ${r.arppu.map((m) => moneyText(Number(m.amount), m.currency)).join(', ')}` : undefined}>
              {r ? (r.arpu?.length ? r.arpu.map((m) => moneyText(Number(m.amount), m.currency)).join(', ') : '—') : pending}
            </span>
            <span role="cell">
              {ctx.canWrite && (
                <IconButton label={`Remove variant ${id.slice(0, 8)}`} onClick={() => removeVariant(id)}>
                  <X aria-hidden="true" />
                </IconButton>
              )}
            </span>
          </div>
        );
      })}
      <div className="lo-ab-table-foot">
        {ctx.canWrite && (
          <Button size="s" variant="ghost" onClick={() => addVariant(env, ctx.doc, (path, value) => ctx.edit(path, value))} icon={<Plus aria-hidden="true" />}>
            Add variant
          </Button>
        )}
        <span className="lo-meta">
          {results ? `${results.players} players assigned in the selected environment${results.includeTesters ? ' (testers included)' : ''} · ${computed(results.computedAt)}` : 'results of the selected environment load when the test is saved'}
        </span>
      </div>
    </div>
  );
}

/**
 * The progress against the required sample (8.4): "N of M required users per variant (D days left at the current pace)",
 * M from the test's uplift / α / β and the Control's observed baseline of the primary metric.
 */
function ProgressLine({ test, results }: { test: Document; results: AbTestResults }) {
  const data = (test.data ?? {}) as Record<string, unknown>;
  const metric = metricOf(data.metric);
  const planner = plannerOf(data);
  const pr = progressOf(metric, results, planner);
  const basis = `Two-sided test at α ${planner.alpha}, power ${(1 - planner.betta).toFixed(2)}, minimum detectable uplift ${(planner.uplift * 100).toFixed(0)} % of the Control's ${metric.label.toLowerCase()}${metric.kind === 'mean' ? '; the standard deviation is estimated from the Control’s conversion and ARPPU' : ''}`;
  if (pr.required === null)
    return (
      <p className="lo-ab-progress lo-note" data-testid="ab-progress" title={basis}>
        {countText(pr.have)} users per variant; the required sample is not estimable yet ({pr.reason}).
      </p>
    );
  const share = Math.min(1, pr.have / pr.required);
  const left = pr.have >= pr.required ? 'enough to decide' : pr.daysLeft === null ? 'no pace yet' : `${countText(pr.daysLeft)} day${pr.daysLeft === 1 ? '' : 's'} left at the current pace`;
  return (
    <p className="lo-ab-progress" data-testid="ab-progress" title={basis}>
      <span className="lo-ab-progress-bar" role="progressbar" aria-label="Required sample" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(share * 100)}>
        <span style={{ width: `${share * 100}%` }} />
      </span>
      <span>
        {countText(pr.have)} of {countText(pr.required)} required users per variant ({left})
      </span>
    </p>
  );
}

/** The significance lines (each variant against the control) led by the primary metric, the progress line, the SRM chip and the Users-per-day chart (8.4). */
function TestStatistics({ test, variantIds, results }: { test: Document; variantIds: string[]; results: AbTestResults }) {
  const { catalog } = useWidgetEnv();
  const define = useDefinitionText();
  const testData = (test.data ?? {}) as Record<string, unknown>;
  const metric = metricOf(testData.metric);
  const { alpha } = plannerOf(testData);
  const nameOf = (id: string) => {
    const doc = catalog.resolve(id);
    const name = (doc?.data as Record<string, Json> | undefined)?.name;
    return typeof name === 'string' && name ? name : doc ? catalog.label(doc) : id.slice(0, 8);
  };
  const variants = results.variants ?? [];
  const order = [...variantIds.filter((id) => variants.some((v) => v.variantId === id)), ...variants.map((v) => v.variantId).filter((id) => !variantIds.includes(id))];
  const dates = Array.from(new Set(variants.flatMap((v) => (v.usersByDay ?? []).map((d) => d.date)))).sort();
  const series = order.map((id) => {
    const r = variants.find((v) => v.variantId === id)!;
    const byDate = new Map((r.usersByDay ?? []).map((d) => [d.date, n(d.users)]));
    return { name: nameOf(id), values: dates.map((d) => byDate.get(d) ?? 0) };
  });
  const srm = results.srm;
  const at = results.computedAt;
  return (
    <div className="lo-ab-stats ab-stats" data-testid="ab-stats">
      <div className="lo-ab-srm">
        {srm?.flagged ? (
          <span data-testid="ab-srm">
            <Badge tone="warning" title={define(results.definitions, ['srm'], at, [`χ² = ${Number(srm.chiSquare).toFixed(2)}, ${pText(srm.p)}; observed ${srm.observed.join(' / ')}, expected ${srm.expected.map((x) => Number(x).toFixed(1)).join(' / ')}`])}>
              <AlertTriangle className="icon-s" aria-hidden="true" /> Sample-ratio mismatch
            </Badge>
          </span>
        ) : srm ? (
          <span className="lo-note" title={define(results.definitions, ['srm'], at, [`χ² = ${Number(srm.chiSquare).toFixed(2)}, ${pText(srm.p)}`])}>
            <CheckCircle2 aria-hidden="true" /> assignments match the weights
          </span>
        ) : null}
      </div>
      <ProgressLine test={test} results={results} />
      {(results.stats ?? []).length === 0 ? (
        <p className="lo-note">No comparison: the test needs a control and at least one other variant with players.</p>
      ) : (
        <ul className="lo-ab-compare ab-significance" aria-label="Significance against the control">
          {(results.stats ?? []).map((st) => {
            const head = `${nameOf(st.variantId)} vs ${nameOf(st.versus)}`;
            const delta = st.conversionDelta === null || st.conversionDelta === undefined ? null : Number(st.conversionDelta);
            const ci = st.conversionInterval;
            const arpu = (st.arpuDelta ?? []).map((a) => `ARPU ${Number(a.amount) < 0 ? '−' : '+'}${moneyText(Math.abs(Number(a.amount)), a.currency)}, ${pText(a.p)}`);
            const text = delta === null ? `${head}: no data` : `${head}: ${signed(delta)} pp conversion, 95 % CI [${ci ? `${signed(Number(ci.low))}, ${signed(Number(ci.high))}` : '—'}], ${pText(st.conversionP)}${arpu.length ? `; ${arpu.join('; ')}` : ''}`;
            const head1 = headlineOf(metric, variants.find((v) => v.variantId === st.versus), variants.find((v) => v.variantId === st.variantId), st);
            const verdict = head1.lift === null ? { word: 'No data', tone: 'neutral' as const } : head1.p === null ? { word: 'No test', tone: 'neutral' as const } : head1.p < alpha ? { word: 'Significant', tone: 'success' as const } : { word: 'Not significant', tone: 'neutral' as const };
            // the secondary figures: conversion and ARPU when they are not the headline
            const secondary: string[] = [];
            if (metric.key !== 'conversion') secondary.push(`Conversion ${delta === null ? '—' : `${signed(delta)} pp`}${ci ? ` (95 % CI ${signed(Number(ci.low))} to ${signed(Number(ci.high))})` : ''}, ${pText(st.conversionP)}`);
            if (metric.key !== 'arpu' && (st.arpuDelta ?? []).length > 0) secondary.push(`ARPU ${(st.arpuDelta ?? []).map((a) => `${Number(a.amount) < 0 ? '−' : '+'}${moneyText(Math.abs(Number(a.amount)), a.currency)}, ${pText(a.p)}`).join(' · ')}`);
            return (
              <li key={st.variantId} className="lo-ab-comparison">
                <div className="stat-strip lo-ab-strip" data-testid={`ab-headline-${st.variantId}`}>
                  <div className="stat">
                    <span className="stat-label">Comparison</span>
                    <span className="stat-value lo-ab-vs">
                      {nameOf(st.variantId)} <span className="muted">vs</span> {nameOf(st.versus)}
                    </span>
                  </div>
                  <div className="stat" title={`The primary metric: ${metric.help}`} data-testid="ab-headline-metric">
                    <span className="stat-label">{metric.label}</span>
                    <span className="stat-value">
                      {head1.variant} <span className="muted">vs {head1.control}</span>
                    </span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Lift ({metric.label})</span>
                    <span className="stat-value">
                      {head1.lift ?? '—'}
                      {head1.interval ? <span className="muted"> 95 % CI {head1.interval}</span> : null}
                    </span>
                  </div>
                  <div className="stat" title={metric.key === 'revenue' ? 'No test: the server computes none for revenue per exposed user' : undefined}>
                    <span className="stat-label">p-value</span>
                    <span className="stat-value">{pValue(head1.p)}</span>
                  </div>
                  <div className="stat" title={`At the ${(alpha * 100).toFixed(alpha < 0.01 ? 1 : 0)} % level: the p-value of the ${metric.label} difference below ${alpha}`}>
                    <span className="stat-label">Significance</span>
                    <span className="stat-value">
                      <Badge tone={verdict.tone}>{verdict.word}</Badge>
                    </span>
                  </div>
                </div>
                {secondary.length > 0 && (
                  <p className="lo-ab-secondary" data-testid={`ab-secondary-${st.variantId}`}>
                    {secondary.join(' · ')}
                  </p>
                )}
                <p className="lo-ab-explain" data-testid={`ab-significance-${st.variantId}`} title={define(results.definitions, ['conversionDelta', 'arpuDelta'], at)}>
                  {text}
                </p>
              </li>
            );
          })}
        </ul>
      )}
      <div className="lo-ab-chart" data-testid="ab-users-per-day" title={define(results.definitions, ['usersByDay'], at)}>
        {dates.length === 0 ? <p className="lo-note">No assignment yet: the Users-per-day chart fills as players are bucketed.</p> : <LineChart series={series} labels={dates} label="Users per day" />}
      </div>
    </div>
  );
}

/** The test's override documents: one block per document patched, a value input per variant column (8.4). */
function OverrideReferences({ ctx, variantIds, overrides }: { ctx: CardContext; variantIds: string[]; overrides: Document[] }) {
  const env = useWidgetEnv();
  const { catalog, workspace } = env;
  const [picking, setPicking] = useState(false);
  const [target, setTarget] = useState<Document | null>(null);
  const priorities = overrides.flatMap((o) => groupsOf(o).map((g) => g.priority));
  const priority = priorities.length ? priorities[0]! : null;
  const setPriority = (p: number) => {
    for (const o of overrides) {
      const groups = groupsOf(o).map((g) => ({ ...g, priority: p }));
      workspace.edit('document', o.id, 'data.groups', groups.map(toGroupJson), { base: baseOf(o), label: catalog.label(o), template: o.template });
    }
  };
  const removeComponent = (id: string) => {
    const node = catalog.resolve(id) as Document | undefined;
    workspace.remove('document', id, node ? baseOf(node) : undefined, { label: 'override row', template: node?.template });
  };
  const removeBlock = (o: Document) => {
    for (const g of groupsOf(o)) {
      for (const r of g.overrides) removeComponent(r);
      if (g.condition) for (const id of collectTree(catalog, g.condition).ids) removeComponent(id);
    }
    workspace.remove('document', o.id, baseOf(o), { label: catalog.label(o), template: o.template });
  };
  return (
    <div className="lo-ab-overrides override-refs" data-testid="ab-overrides">
      {overrides.length > 0 && (
        <div className="lo-ab-subhead">
          {priority !== null && (
            <span className="lo-inline-field" title="The priority of every override group of this test (each variant has its own group)">
              <Field label="Priority" inline>
                <NumberInput size="s" kind="int" value={priority} disabled={!ctx.canWrite} aria-label="Override groups priority" onCommit={(v) => setPriority(Number(v ?? 100))} />
              </Field>
            </span>
          )}
          <span className="spacer" />
          {ctx.canWrite && (
            <Button size="s" onClick={() => setPicking(true)} disabled={variantIds.length === 0} icon={<Plus aria-hidden="true" />}>
              Add override
            </Button>
          )}
        </div>
      )}
      {overrides.length === 0 && (
        <div className="lo-ab-empty" role="note">
          <span className="empty-icon" aria-hidden="true">
            <Layers />
          </span>
          <span className="lo-ab-empty-text">
            <strong>No overrides yet</strong>
            <span>Every variant shows the same content; add an override to let a variant change values.</span>
          </span>
          {ctx.canWrite && (
            <Button size="s" onClick={() => setPicking(true)} disabled={variantIds.length === 0} icon={<Plus aria-hidden="true" />}>
              Add override
            </Button>
          )}
        </div>
      )}
      {overrides.map((o) => (
        <OverrideBlock key={o.id} override={o} variantIds={variantIds} canWrite={ctx.canWrite} onRemove={() => removeBlock(o)} onRemoveRow={removeComponent} />
      ))}
      {picking && (
        <ReferencePicker
          param={{ template: null, name: 'document', displayName: 'Document to override', type: 'document' }}
          selected={[]}
          onClose={() => setPicking(false)}
          onPick={(ids) => {
            setPicking(false);
            if (ids[0]) void resolveDocument(catalog, ids[0]).then((doc) => doc && setTarget(doc));
          }}
        />
      )}
      {target && (
        <ParameterPickDialog
          doc={target}
          title="Params to override"
          confirmLabel="Create override (staged)"
          onClose={() => setTarget(null)}
          onPick={(leaves) => {
            createVariantOverride(env, ctx.doc, variantIds, target, leaves);
            setTarget(null);
          }}
        />
      )}
    </div>
  );
}

function OverrideBlock({ override, variantIds, canWrite, onRemove, onRemoveRow }: { override: Document; variantIds: string[]; canWrite: boolean; onRemove: () => void; onRemoveRow: (id: string) => void }) {
  const { catalog, workspace } = useWidgetEnv();
  const groups = groupsOf(override);
  // the group of each variant, by the ABTestCondition in its condition tree
  const groupOf = new Map<string, number>();
  for (const [i, g] of groups.entries()) {
    for (const v of variantIds) {
      const hit = g.condition ? variantOfTree(catalog, g.condition) : null;
      if (hit === v) groupOf.set(v, i);
    }
  }
  // the rows by (document, path) across the groups
  const pairs = new Map<string, { document: string; path: string; byVariant: Map<string, string> }>();
  for (const [i, g] of groups.entries()) {
    const variant = Array.from(groupOf.entries()).find(([, gi]) => gi === i)?.[0];
    for (const id of g.overrides) {
      const row = patchOf(catalog.resolve(id), id);
      if (!row || !row.document) continue;
      const key = `${row.document}\u0000${row.path.toLowerCase()}`;
      const entry = pairs.get(key) ?? { document: row.document, path: row.path, byVariant: new Map<string, string>() };
      if (variant) entry.byVariant.set(variant, id);
      pairs.set(key, entry);
    }
  }
  const removePair = (entry: { byVariant: Map<string, string> }) => {
    const ids = new Set(entry.byVariant.values());
    for (const id of ids) onRemoveRow(id);
    const next = groups.map((g) => ({ ...g, overrides: g.overrides.filter((x) => !ids.has(x)) }));
    workspace.edit('document', override.id, 'data.groups', next.map(toGroupJson), { base: baseOf(override), label: catalog.label(override), template: override.template });
  };
  return (
    <div className="override-block lo-ab-override" data-testid={`ab-override-${override.id}`}>
      <div className="lo-ab-override-head">
        <Layers aria-hidden="true" />
        <strong>{catalog.label(override)}</strong>
        <span className="spacer" />
        {canWrite && (
          <IconButton label={`Remove override ${catalog.label(override)}`} onClick={onRemove}>
            <X aria-hidden="true" />
          </IconButton>
        )}
      </div>
      <div className="override-matrix lo-ab-matrix" role="table" aria-label={`Values of ${catalog.label(override)}`}>
        <div className="matrix-row matrix-head" role="row">
          <span role="columnheader">Field</span>
          {variantIds.map((v) => {
            const doc = catalog.resolve(v);
            return (
              <span key={v} role="columnheader">
                {doc ? catalog.label(doc) : v.slice(0, 8)}
              </span>
            );
          })}
          <span role="columnheader" />
        </div>
        {Array.from(pairs.values()).map((entry) => {
          const target = catalog.resolve(entry.document) as Document | undefined;
          const param = target ? paramAt(catalog, catalog.templates.get(target.template), entry.path) : undefined;
          return (
            <div key={`${entry.document}.${entry.path}`} className="matrix-row" role="row">
              <span role="cell" className="lo-ab-field" title={target ? catalog.label(target) : entry.document}>
                <span className="lo-ab-doc">{target ? catalog.label(target) : entry.document.slice(0, 8)}</span>
                <span>{param ? (param.displayName ?? param.name) : entry.path}</span>
              </span>
              {variantIds.map((v) => {
                const id = entry.byVariant.get(v);
                const node = id ? catalog.resolve(id) : undefined;
                const row = id ? patchOf(node, id) : null;
                return (
                  <span key={v} role="cell">
                    {row && param ? (
                      <FieldWidget param={{ ...param, name: 'value', displayName: `${param.displayName ?? param.name} for ${v.slice(0, 8)}` }} value={row.value} compact disabled={!canWrite} onCommit={(val) => workspace.edit('document', row.id, 'data.value', val, { base: node ? baseOf(node as Document) : undefined, label: 'override row', template: node?.template })} />
                    ) : (
                      <span className="lo-ab-base">{param && target ? formatValue(param, (target.data as Record<string, Json>)[entry.path], catalog.format) : '—'}</span>
                    )}
                  </span>
                );
              })}
              <span role="cell">
                {canWrite && (
                  <IconButton label={`Remove ${entry.path} from the override`} onClick={() => removePair(entry)}>
                    <X aria-hidden="true" />
                  </IconButton>
                )}
              </span>
            </div>
          );
        })}
        {pairs.size === 0 && <p className="lo-override-empty">No rows: the override changes no field yet.</p>}
      </div>
    </div>
  );
}

/** The variant an ABTestCondition tree names (the first found). */
function variantOfTree(catalog: ReturnType<typeof useContent>, rootId: string, seen = new Set<string>()): string | null {
  if (seen.has(rootId)) return null;
  seen.add(rootId);
  const node = catalog.resolve(rootId);
  if (!node) return null;
  const d = (node.data ?? {}) as Record<string, Json>;
  if (node.template === 'SmartObjects.Conditions.ABTestCondition') return typeof d.variant === 'string' ? d.variant : null;
  const children: string[] = [];
  if (Array.isArray(d.conditions)) children.push(...d.conditions.filter((c): c is string => typeof c === 'string'));
  if (typeof d.condition === 'string') children.push(d.condition);
  for (const c of children) {
    const hit = variantOfTree(catalog, c, seen);
    if (hit) return hit;
  }
  return null;
}
